import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { testIds as clpTestIds } from 'modules/creativeLibrary/components/CreativeLibraryPanel';
import { usePanelContext } from '../../contexts/Panel';
import { useTranslations } from '../../hooks/useTranslations';
import { PanelIntent, PanelView } from '../../types';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';
import { useCloseModalOverlayContext } from 'modules/creativeLibrary/contexts/CloseModalOverlayContext';

export const testIds = {
  btnCancel: 'cl-panel-btn-cancel',
  btnSave: 'cl-panel-takeover-btn-save',
  btnUpload: 'cl-panel-btn-upload',
  container: 'cl-panel-takeover-footer-container',
};

type ButtonConfig = {
  text: string;
  onClick: () => void;
  variant: BUTTON_VARIANTS;
  testId?: string;
  isDisabled?: boolean;
  shouldShow: boolean;
  isLoading?: boolean;
};

export const PanelFooter = () => {
  const { t } = useTranslations();
  const {
    assetUrl,
    imageAttrState,
    imgMetadata,
    nonCustomizableAttrs,
    onClose,
    onInsert,
    onSelect,
    panelIntent,
    panelView,
  } = usePanelContext();
  const { handleUploadPending, pendingUploadCount, isUploading } = useUploadAssetsContext();
  const { onCloseModal } = useCloseModalOverlayContext();

  const handleUpload = () => {
    handleUploadPending();
  };

  // inserting (or replacing) an image into the editor
  const handleInsert = () => {
    if (onInsert) {
      onInsert({ ...imageAttrState, ...nonCustomizableAttrs });
    }
    onClose();
  };

  // inserting (or replacing) the image url into a form field
  const handleSelect = () => {
    if (onSelect) {
      onSelect(assetUrl, imgMetadata);
      onClose();
    }
  };

  // TODO: import from url: has cancel and upload button
  const buttonConfigs: ButtonConfig[] = [
    // Cancel button
    {
      text: t('button.cancel'),
      onClick: () => onCloseModal(panelIntent === PanelIntent.Edit),
      variant: BUTTON_VARIANTS.GHOST,
      shouldShow: true,
    },
    // Upload button
    {
      text: !pendingUploadCount
        ? t('button.uploadImages_one')
        : t('button.uploadImages', { count: pendingUploadCount }),
      onClick: handleUpload,
      variant: BUTTON_VARIANTS.PRIMARY,
      testId: testIds.btnUpload,
      isLoading: isUploading,
      shouldShow: panelIntent === PanelIntent.Upload,
      isDisabled: pendingUploadCount === 0,
    },
    // Insert/save button
    {
      text: panelIntent === PanelIntent.Insert ? t('button.insert') : t('button.save'),
      onClick: handleInsert,
      variant: BUTTON_VARIANTS.PRIMARY,
      testId: clpTestIds.panelFooter.footerBtnInsert,
      shouldShow:
        !!onInsert &&
        panelView === PanelView.Detail &&
        (panelIntent === PanelIntent.Insert || panelIntent === PanelIntent.Edit),
    },
    // Select button
    {
      text: panelIntent === PanelIntent.EditSelect ? t('button.save') : t('button.select'),
      onClick: handleSelect,
      variant: BUTTON_VARIANTS.PRIMARY,
      testId: clpTestIds.panelFooter.footerBtnSelect,
      shouldShow: !!onSelect && panelView === PanelView.Detail,
    },
  ];

  return (
    <>
      {buttonConfigs
        .filter((config) => config.shouldShow)
        .map((config, index) => (
          <Button
            key={index}
            data-test={config.testId}
            isDisabled={config.isDisabled}
            onClick={config.onClick}
            size={BUTTON_SIZES.MEDIUM}
            variant={config.variant}
            isLoading={config.isLoading}
          >
            <span>{config.text}</span>
          </Button>
        ))}
    </>
  );
};
